import { useEffect, useMemo, useRef, useState } from "react";
import { usePageTag } from "../app/usePageTag";
import { consumePendingRoom } from "../features/map/roomFocus";
import { postToMap } from "../features/map/mapBridge";
import { useT } from "../features/i18n/useT";

const defaultMapBase = "/map/";

export default function MapPage() {
  usePageTag("map");
  const t = useT();

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [room, setRoom] = useState<string | null>(null);
  const [mapStatus, setMapStatus] = useState<"loading" | "ready" | "missing">("loading");

  const mapBase = useMemo(() => {
    const raw = import.meta.env.VITE_MAP_BASE || defaultMapBase;
    return raw.endsWith("/") ? raw : `${raw}/`;
  }, []);

  useEffect(() => {
    const readRoom = () => {
      const params = new URLSearchParams(window.location.search);
      const fromQuery = params.get("search");
      return fromQuery?.trim() ? fromQuery.trim() : consumePendingRoom();
    };

    setRoom(readRoom());
    const on = () => setRoom(readRoom());
    window.addEventListener("app:navigate", on);
    window.addEventListener("popstate", on);
    return () => {
      window.removeEventListener("app:navigate", on);
      window.removeEventListener("popstate", on);
    };
  }, []);

  useEffect(() => {
    let active = true;
    fetch(`${mapBase}index.html`, { method: "HEAD" })
      .then((res) => {
        if (!active) return;
        setMapStatus(res.ok || res.status === 405 ? "ready" : "missing");
      })
      .catch(() => {
        if (!active) return;
        setMapStatus("missing");
      });
    return () => {
      active = false;
    };
  }, [mapBase]);

  const focus = (r: string) => {
    postToMap(iframeRef.current, { type: "FOCUS_ROOM", room: r });
  };

  const fillMapSearch = (r: string) => {
    postToMap(iframeRef.current, { type: "SET_SEARCH", room: r });
  };

  useEffect(() => {
    if (!room || mapStatus !== "ready") return;
    focus(room);
    window.setTimeout(() => fillMapSearch(room), 150);
    window.setTimeout(() => fillMapSearch(room), 600);
  }, [room, mapStatus]);

  return (
    <div className="page map-page">
      {room ? (
        <div className="map-toolbar">
          <div className="row wrap">
            <button className="btn ghost" onClick={() => focus(room)}>
              {t("map.focus")}
            </button>
          </div>
        </div>
      ) : null}

      <div className="muted small map-hint">
        {room ? (
          <>
            {t("map.focusing")} <b>{room}</b>
          </>
        ) : (
          t("map.hint")
        )}
      </div>

      {mapStatus === "missing" ? (
        <div className="empty">
          <div className="empty-title">{t("map.title")}</div>
          <div className="muted small">
            {t("map.missing")} <b>{`${mapBase}index.html`}</b>.
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <iframe
            ref={iframeRef}
            title={t("map.iframeTitle")}
            src={`${mapBase}index.html`}
            className="map-iframe"
            sandbox="allow-scripts allow-forms allow-popups allow-same-origin"
            onLoad={() => {
              if (room) {
                focus(room);
                // Let the map UI boot, then inject search text
                window.setTimeout(() => fillMapSearch(room), 150);
                window.setTimeout(() => fillMapSearch(room), 600);
              }
            }}
          />
        </div>
      )}
    </div>
  );
}

